import React from 'react';
import moment from 'moment';

import './App.css';

import { Switch, Route, Router, Redirect } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import makeStyles from '@mui/styles/makeStyles';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';

import LoginPage from './pages/LoginPage';
import CarModal from './components/CarModal/CarModal';
import CustomerModal from './pages/CustomerPage/CustomerModal';
import LeadModal from './components/LeadModal';
import CreditAppModal from './pages/CreditAppPage/CreditAppModal';
import ServiceOrderModal from './components/ServiceOrderModal';
import WriteCheckModal from './components/WriteCheckModal';
import PayNtoModal from './components/PayNtoModal';
import ShippingLoadModal from './components/ShippingLoadModal';
import Header from './components/Header';
import PhoneInboundAlerts from './components/PhoneInboundAlerts';

import useWindowSize from './utilities/useWindowSize.js';
import history from './utilities/history';
import { startPinIdleWatch, savePostLoginRedirect } from './utilities/session';
import { confirmSignedOut } from './utilities/authGuard';

import Camera, { FACING_MODES } from 'react-html5-camera-photo';
import 'react-html5-camera-photo/build/css/index.css';

import MuiAlert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { StateManager } from './utilities/stateManager';
import { canAccessPhone } from './pages/PhonePage/phone';
import { PermissionsContext } from './utilities/PermissionsContext';
import usePermissions from './utilities/usePermissions';
import firebase from './utilities/firebase';
import constants from './utilities/constants';
import store from './utilities/store';

import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AdminToast from './components/AdminToast';
import MechanicToast from './components/MechanicToast';
import DevBranchBadge from './components/DevBranchBadge';

const CreateUserPage = React.lazy(() => import('./pages/CreateUserPage'));
const CheckForm = React.lazy(() => import('./pages/CheckFormPage.js'));
const ServiceBoard = React.lazy(() => import('./pages/ServiceKanbanPage'));
const ScanPage = React.lazy(() => import('./pages/ScanPage'));
const InputPage = React.lazy(() => import('./pages/InputPage'));
const AccountingPage = React.lazy(() => import('./pages/AccountingPage'));
const DealDashboardPage = React.lazy(() => import('./pages/DealDashboardPage'));
const SalesTaxPage = React.lazy(() => import('./pages/SalesTaxPage'));
const PerformanceDashboard = React.lazy(() => import('./pages/PerformanceDashboard'));
const PayrollPage = React.lazy(() => import('./pages/PayrollPage'));
const PaymentPage = React.lazy(() => import('./pages/PaymentPage'));
const CreditAppPrintPage = React.lazy(() => import('./pages/CreditAppPage/CreditAppPrintPage'));
const DepositReceipt = React.lazy(() => import('./pages/ReceiptPage/DepositReceipt'));
const PaymentStatement = React.lazy(() => import('./pages/ReceiptPage/PaymentStatement'));
const InvoiceRecap = React.lazy(() => import('./pages/ReceiptPage/InvoiceRecap'));
const PaymentConfirmationPage = React.lazy(() => import('./pages/PaymentConfirmationPage'));
const PipelinePage = React.lazy(() => import('./pages/PipelinePage'));
const ServicePipelinePage = React.lazy(() => import('./pages/ServicePipelinePage'));
const PartsPipelinePage = React.lazy(() => import('./pages/PartsPipelinePage'));
const TimesheetPage = React.lazy(() => import('./pages/TimesheetPage'));
const BackOfficePage = React.lazy(() => import('./pages/BackOfficePage'));
const InventoryPage = React.lazy(() => import('./pages/InventoryPage'));
const ServicePlanPage = React.lazy(() => import('./pages/ServicePlanPage'));
const ServicePrioritiesPage = React.lazy(() => import('./pages/ServicePrioritiesPage'));
const CreditCardStatementPage = React.lazy(() => import('./pages/CreditCardStatementPage'));
const SalesInboxPage = React.lazy(() => import('./pages/SalesInboxPage'));
const PhonePage = React.lazy(() => import('./pages/PhonePage'));
const ShippingDashboardPage = React.lazy(() => import('./pages/ShippingDashboardPage/'));
const SignatureTemplatesPage = React.lazy(() => import('./pages/SignatureTemplatesPage'));
const FeedHealthPage = React.lazy(() => import('./pages/FeedHealthPage'));
const InventoryEmailPage = React.lazy(() => import('./pages/InventoryEmailPage'));
const DealTrackerPage = React.lazy(() => import('./pages/DealTrackerPage'));
const ShippingLoadPage = React.lazy(() => import('./pages/ShippingLoadPage/'));
const LoadPlannerPage = React.lazy(() => import('./pages/LoadPlannerPage'));
const ServiceDashboardPage = React.lazy(() => import('./pages/ServiceDashboardPage/'));
const PartsDashboardPage = React.lazy(() => import('./pages/PartsDashboardPage/'));
const MechanicOrdersPage = React.lazy(() => import('./pages/MechanicOrdersPage/'));
const MechanicClockPage = React.lazy(() => import('./pages/MechanicClockPage'));
const IntakeQueuePage = React.lazy(() => import('./pages/IntakeQueuePage'));
const PaintJobsDashboardPage = React.lazy(() => import('./pages/PaintJobsDashboardPage/'));
const ServiceAgingPage = React.lazy(() => import('./pages/ServiceAgingPage/'));
const ListPage = React.lazy(() => import('./pages/ListPage'));
const VendorPage = React.lazy(() => import('./pages/VendorPage'));
const VendorDashboard = React.lazy(() => import('./pages/VendorDashboard'));
const ShppingForm = React.lazy(() => import('./pages/ShippingPage.js'));
const MechanicSummaryPage = React.lazy(() => import('./pages/MechanicSummaryPage'));
const ThankYouPage = React.lazy(() => import('./pages/ThankYouPage'));
const LeadsPage = React.lazy(() => import('./pages/LeadsPage'));
const CreditAppsPage = React.lazy(() => import('./pages/CreditAppsPage'));
const CreditAppSendsPage = React.lazy(() => import('./pages/CreditAppSendsPage'));
const InspectionPage = React.lazy(() => import('./pages/InspectionPage'));
const AssignInventoryPage = React.lazy(() => import('./pages/AssignInventoryPage.js'));
const EstimatePage = React.lazy(() => import('./pages/EstimatePage.js'));
const ServiceStatusPage = React.lazy(() => import('./pages/ServiceStatusPage.js'));
const ConsignmentDashboardPage = React.lazy(() => import('./pages/ConsignmentDashboardPage'));
const SearchPage = React.lazy(() => import('./pages/SearchPage'));
const AddLeadPage = React.lazy(() => import('./pages/AddLeadPage'));
const CarPerformancePage = React.lazy(() => import('./pages/CarPerformancePage'));
const LeadSourcesPage = React.lazy(() => import('./pages/LeadSourcesPage'));
const RepPerformanceDashboard = React.lazy(() => import('./pages/RepPerformanceDashboard'));
const ContractorPaymentPage = React.lazy(() => import('./pages/ContractorPaymentPage'));
const PartsInventoryPage = React.lazy(() => import('./pages/PartsInventoryPage'));
const MechanicQueuePage = React.lazy(() => import('./pages/MechanicQueuePage'));
const MechanicBoardPage = React.lazy(() => import('./pages/MechanicBoardPage'));
const InventorySnapshotPage = React.lazy(() => import('./pages/InventorySnapshotPage'));
const AccountsPayablePage = React.lazy(() => import('./pages/AccountsPayablePage'));
const AccountsReceivablePage = React.lazy(() => import('./pages/AccountsReceivablePage'));
const TitleProcessingPage = React.lazy(() => import('./pages/TitleProcessingPage'));
const UserManagementPage = React.lazy(() => import('./pages/UserManagementPage'));
const BanksPage = React.lazy(() => import('./pages/BanksPage'));
const LeadAssignmentPage = React.lazy(() => import('./pages/LeadAssignmentPage'));
const RonCheckinPage = React.lazy(() => import('./pages/RonCheckinPage'));
const PriceStrategyPage = React.lazy(() => import('./pages/PriceStrategyPage'));
const DetailDashboardPage = React.lazy(() => import('./pages/DetailDashboardPage'));
const ServicePerformancePage = React.lazy(() => import('./pages/ReportsPage/ServicePerformance'));
const SalesPerformancePage = React.lazy(() => import('./pages/ReportsPage/SalesPerformance'));
const InventoryPerformancePage = React.lazy(() => import('./pages/ReportsPage/InventoryPerformance'));
const CarDemandPage = React.lazy(() => import('./pages/ReportsPage/CarDemand'));
const FundingPerformancePage = React.lazy(() => import('./pages/ReportsPage/FundingPerformance'));
const InventoryDistributionPage = React.lazy(() => import('./pages/ReportsPage/InventoryDistribution'));
const FilesPage = React.lazy(() => import('./pages/FilesPage'));
const BillsPage = React.lazy(() => import('./pages/BillsPage'));



function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height
  };
}

const App = () => {

  const classes = useStyles();

  const size = useWindowSize();
  const [message, setMessage] = React.useState("Data posted successfully!");
  StateManager.setAlertMessage = setMessage;
  const [open, setOpen] = React.useState(false);
  StateManager.openAlert = setOpen;
  const [severity, setSeverity] = React.useState('success');
  StateManager.setAlertSeverity = setSeverity;
  StateManager.setAlertAndOpen = (message, severity) => {
    if(severity) setSeverity(severity);
    setMessage(message);
    setOpen(true);
    // setTimeout(handleClose, 3000);
  }
  const [loading, setLoading] = React.useState(false);
  StateManager.loading = loading;
  StateManager.setLoading = setLoading;

  const [pageLoading, setPageLoading] = React.useState(false);
  StateManager.pageLoading = pageLoading;
  StateManager.setPageLoading = setPageLoading;

  const [maxWidth, setMaxWidth] = React.useState("lg");
  StateManager.setMaxWidth = setMaxWidth;

  const [title, setTitle] = React.useState(document.title);
  StateManager.title = title;
  const carModalOpenRef = React.useRef(false);
  StateManager.setTitle = (newTitle) => {
    if (newTitle == null) return;          // never set a null/undefined title
    if (carModalOpenRef.current) return;  // don't let any page clobber the title while the modal is up
    setTitle(newTitle);
  };

  const [windowDimensions, setWindowDimensions] = React.useState(getWindowDimensions());
  StateManager.windowDimensions = windowDimensions;

  // Pages that handle ?so= natively — App-level SO modal stays closed on these
  const SO_NATIVE_PAGES = ['/service-dashboard', '/service-pipeline', '/service-aging'];
  const isOnSoNativePage = (pathname) => SO_NATIVE_PAGES.some(p => pathname.startsWith(p));

  // Pages that drive ?load= themselves (dashboard selection, full load page) —
  // the global load modal stays closed there so it doesn't double-open.
  const LOAD_NATIVE_PAGES = ['/shipping-dashboard', '/load'];
  const isOnLoadNativePage = (pathname) => LOAD_NATIVE_PAGES.some(p => pathname.startsWith(p));

  // Global car modal — driven by ?car= URL param
  const [carModalStock, setCarModalStock] = React.useState(() => {
    return new URLSearchParams(window.location.search).get('car') || null;
  });

  const closeCarModal = React.useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete('car');
    url.searchParams.delete('tab');
    history.replace(url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : ''));
  }, []);

  StateManager.openCarModal = (stockNumber, tab) => {
    if (!canOpen('car')) return denyOpen('cars');
    const url = new URL(window.location.href);
    url.searchParams.set('car', stockNumber);
    if (tab) url.searchParams.set('tab', tab);
    history.push(url.pathname + '?' + url.searchParams.toString());
  };

  // Global Service Order modal — driven by ?so= URL param (skipped on SO_NATIVE_PAGES).
  // Optional ?soTabs= (comma-separated) restricts which tabs render.
  const [soModalStock, setSoModalStock] = React.useState(() => {
    if (isOnSoNativePage(window.location.pathname)) return null;
    return new URLSearchParams(window.location.search).get('so') || null;
  });
  const [soModalAllowedTabs, setSoModalAllowedTabs] = React.useState(() => {
    const v = new URLSearchParams(window.location.search).get('soTabs');
    return v ? v.split(',').filter(Boolean) : null;
  });

  const closeSoModal = React.useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete('so');
    url.searchParams.delete('soTabs');
    history.replace(url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : ''));
  }, []);

  StateManager.openServiceOrderModal = (stockNumber, options = {}) => {
    if (!stockNumber) { closeSoModal(); return; }
    if (!canOpen('service-order')) return denyOpen('service orders');
    const url = new URL(window.location.href);
    url.searchParams.set('so', stockNumber);
    if (options.allowedTabs && options.allowedTabs.length) {
      url.searchParams.set('soTabs', options.allowedTabs.join(','));
    } else {
      url.searchParams.delete('soTabs');
    }
    history.push(url.pathname + '?' + url.searchParams.toString());
  };

  // Global Write Check modal — admin only. Driven in-memory (not a URL param)
  // since it carries the selected purchase objects; openable from anywhere a
  // purchase/expense/NTO is interfaced.
  const [checkPurchases, setCheckPurchases] = React.useState(null);
  const closeWriteCheckModal = React.useCallback(() => setCheckPurchases(null), []);
  StateManager.openWriteCheckModal = (purchases) => {
    if (!StateManager.isAdmin()) return;
    const list = (Array.isArray(purchases) ? purchases : [purchases]).filter(Boolean);
    if (!list.length) return;
    setCheckPurchases(list);
  };

  // Global Pay NTO modal — admin only. Carries the selected NTO payable object,
  // so it is held in memory rather than a URL param, and opens from anywhere an
  // NTO purchase is interfaced.
  const [ntoPayable, setNtoPayable] = React.useState(null);
  const closePayNtoModal = React.useCallback(() => setNtoPayable(null), []);
  StateManager.openPayNtoModal = (payable) => {
    if (!StateManager.isAdmin()) return;
    if (!payable || !payable.id) return;
    setNtoPayable(payable);
  };

  // Global customer modal — driven by ?customer= URL param
  const [customerModalId, setCustomerModalId] = React.useState(() => {
    return new URLSearchParams(window.location.search).get('customer') || null;
  });

  const closeCustomerModal = React.useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete('customer');
    url.searchParams.delete('customerTab');
    history.replace(url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : ''));
  }, []);

  StateManager.openCustomerModal = (customerId, tab) => {
    if (!canOpen('customer')) return denyOpen('customers');
    if (!constants.isValidCustomerId(customerId)) return;
    const url = new URL(window.location.href);
    url.searchParams.set('customer', customerId);
    if (tab) url.searchParams.set('customerTab', tab);
    history.push(url.pathname + '?' + url.searchParams.toString());
  };

  // Global lead modal — driven by ?lead= URL param
  const [leadModalId, setLeadModalId] = React.useState(() => {
    return new URLSearchParams(window.location.search).get('lead') || null;
  });

  const closeLeadModal = React.useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete('lead');
    url.searchParams.delete('leadTab');
    history.replace(url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : ''));
  }, []);

  StateManager.openLeadModal = (leadId, tab) => {
    if (!leadId) { closeLeadModal(); return; }
    if (!canOpen('lead')) return denyOpen('leads');
    const url = new URL(window.location.href);
    url.searchParams.set('lead', leadId);
    if (tab) url.searchParams.set('leadTab', tab);
    history.push(url.pathname + '?' + url.searchParams.toString());
  };

  // Global shipping-load modal — driven by ?load= URL param (skipped on
  // LOAD_NATIVE_PAGES, which manage ?load= themselves).
  const [loadModalId, setLoadModalId] = React.useState(() => {
    if (isOnLoadNativePage(window.location.pathname)) return null;
    return new URLSearchParams(window.location.search).get('load') || null;
  });

  const closeLoadModal = React.useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete('load');
    history.replace(url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : ''));
  }, []);

  StateManager.openLoadModal = (loadId) => {
    if (!loadId) { closeLoadModal(); return; }
    if (!canOpen('load')) return denyOpen('shipping loads');
    // The full load page takes its id from the path, not ?load=, so setting the
    // param there would change the URL and open nothing. Navigate to the load.
    if (window.location.pathname.startsWith('/load')) {
      history.push('/load/' + loadId);
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('load', loadId);
    history.push(url.pathname + '?' + url.searchParams.toString());
  };

  // Global credit-app modal — driven by ?capp= URL param. (Distinct from the
  // ?creditApp= param the send-history page uses for its own send filtering.)
  const [creditAppModalId, setCreditAppModalId] = React.useState(() => {
    return new URLSearchParams(window.location.search).get('capp') || null;
  });

  const closeCreditAppModal = React.useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete('capp');
    history.replace(url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : ''));
  }, []);

  StateManager.openCreditAppModal = (creditAppId) => {
    if (!creditAppId) { closeCreditAppModal(); return; }
    if (!canOpen('credit-app')) return denyOpen('credit applications');
    const url = new URL(window.location.href);
    url.searchParams.set('capp', creditAppId);
    history.push(url.pathname + '?' + url.searchParams.toString());
  };

  const [showCamera, setCamera] = React.useState(false);
  StateManager.toggleCamera = () => setCamera(!showCamera);
  // StateManager.photoHandler = () => null;

  const [authReady, setAuthReady] = React.useState(false);

  const storedPerms = {
    whiteList: JSON.parse(localStorage.getItem('whiteList') || sessionStorage.getItem('whiteList') || '[]'),
    userType: localStorage.getItem('userType') || sessionStorage.getItem('userType') || '',
  };
  // `ready` says whether these permissions are the user's real ones or just an empty
  // placeholder. ProtectedRoute needs the difference: empty-and-unresolved has to wait,
  // empty-and-resolved is a genuine "not allowed" and redirects. Without it, every page
  // load that arrived before the users/{uid} snapshot bounced the user to the home page
  // — which is exactly what firebase.js's self-heal produces, since it restores
  // `authed` without restoring `whiteList`.
  const [permissions, setPermissions] = React.useState({
    ...storedPerms,
    ready: !!(storedPerms.userType || storedPerms.whiteList.length),
  });

  // Whether the current user may open one of the global modals. Deliberately the same
  // test ProtectedRoute applies to pages — isAllowed(key) || isAdmin() — so a modal and
  // its page can't disagree. Used both to gate the render below and to refuse the
  // StateManager.open*Modal helpers, so a link carrying ?customer=<id> is inert for
  // someone without the permission.
  const canOpen = React.useCallback((key) => (
    permissions.userType === 'admin' || (permissions.whiteList || []).includes(key)
  ), [permissions]);

  const denyOpen = (label) => StateManager.setAlertAndOpen(
    `You don't have permission to view ${label}`, 'error',
  );

  // ProtectedRoute gates on BOTH the `authed` storage key and these permissions
  // (`isAllowed(key) || isAdmin()`), and sends anyone failing either check to "/",
  // i.e. the home/inventory page. So an empty `permissions` bounces a user off
  // whatever page they were on just as effectively as a logout — without ever
  // showing the login screen.
  //
  // Three guards keep a transient `null` user from doing that:
  //  1. Ignore the null that precedes the first restore (hadUser).
  //  2. Send every null after it through confirmSignedOut, the same verdict
  //     firebase.js uses for the `authed` key. A token refresh that lost the
  //     network or a woken mobile tab can emit a null mid-session; keeping the
  //     last-known permissions rides those out instead of redirecting.
  //  3. Ignore a snapshot for a missing user doc. Firestore reports one while its
  //     cache is cold, and treating it as "no permissions" bounced the user home.
  React.useEffect(() => {
    let unsubUser = null;
    let hadUser = false;
    let cancelPendingSignOut = null;

    const unsubAuth = firebase.auth().onAuthStateChanged((user) => {
      setAuthReady(true);

      if (user) {
        if (cancelPendingSignOut) {
          cancelPendingSignOut();
          cancelPendingSignOut = null;
        }
        hadUser = true;
        if (unsubUser) { unsubUser(); unsubUser = null; }
        unsubUser = firebase.firestore().doc(`users/${user.uid}`).onSnapshot(snap => {
          // No doc means Firestore has nothing to say yet, not that the user lost
          // every permission. Keep what we have.
          if (!snap.exists) return;
          const data = snap.data() || {};
          const { whiteList = [], userType = '', firstName = '', lastName = '' } = data;
          const userName = [firstName, lastName].filter(Boolean).join(' ').trim();
          setPermissions({ whiteList, userType, ready: true });
          StateManager.syncPermissions(whiteList, userType, userName);
          StateManager.archived = data.archived === true;
          // Vendor accounts are scoped to one vendor; the vendor pages read this
          // rather than the :vendor URL segment so one vendor can't view another.
          // Mirrored to storage so it survives a reload and is readable during the
          // first render, before this listener runs (see stateManager constructor).
          StateManager.vendor = data.vendor || '';
          const storage = localStorage.getItem('authed') ? localStorage : sessionStorage;
          storage.setItem('whiteList', JSON.stringify(whiteList));
          storage.setItem('userType', userType);
          if (userName) storage.setItem('userName', userName);
          if (data.vendor) storage.setItem('vendor', data.vendor);
          else storage.removeItem('vendor');
        }, () => {
          // A listen error (rules, offline) leaves the last-known permissions in
          // place, but the gate has to stop waiting or the page hangs on a spinner.
          setPermissions(prev => ({ ...prev, ready: true }));
        });
        return;
      }

      // Nothing to lose yet, or a verdict is already pending on an earlier null.
      if (!hadUser || cancelPendingSignOut) return;

      cancelPendingSignOut = confirmSignedOut(
        () => firebase.auth().currentUser,
        () => {
          cancelPendingSignOut = null;
          hadUser = false;
          if (unsubUser) { unsubUser(); unsubUser = null; }
          // ready: true — they really are signed out, so ProtectedRoute should send
          // them to the login screen rather than wait for permissions that
          // are not coming.
          setPermissions({ whiteList: [], userType: '', ready: true });
        },
      );
    });

    return () => {
      if (cancelPendingSignOut) cancelPendingSignOut();
      unsubAuth();
      if (unsubUser) unsubUser();
    };
  }, []);

  // Never wait forever. ProtectedRoute holds a spinner while permissions are
  // unresolved, so if neither the snapshot nor its error callback ever arrives — a
  // tablet with no signal is the usual case — stop waiting and let the normal
  // permission check run on whatever is in storage.
  React.useEffect(() => {
    const timer = setTimeout(
      () => setPermissions(prev => (prev.ready ? prev : { ...prev, ready: true })),
      10 * 1000,
    );
    return () => clearTimeout(timer);
  }, []);

  // Idle timeout for PIN sessions on shared devices (see utilities/session.js).
  // No-op for email/password sessions, so it costs nothing for normal users.
  React.useEffect(() => startPinIdleWatch(), []);

  // Keep constants.sales in sync with all sales-type users in Firestore so user
  // management changes take effect. This populates the manual lead-assignment
  // dropdowns; auto-assign rotation is controlled separately on the Lead
  // Management page (pauseLeads), so paused reps still show up for manual assignment.
  React.useEffect(() => {
    store.getSalesUsers().then(reps => {
      if (reps.length > 0) constants.sales = reps;
    });
  }, []);

  // Keep constants.mechanics in sync with the `mechanics` collection so name/rate
  // edits on the User Management page take effect app-wide (service-line cost math,
  // payroll, dropdowns, id→name lookups) without a redeploy. Falls back to the
  // hardcoded defaults when the collection is empty.
  React.useEffect(() => {
    store.getMechanics().then(list => {
      if (list.length > 0) constants.setMechanics(list);
    }).catch(() => {});
  }, []);

  // Load per-lender config (emails, CC, invoice format) from the `lenders`
  // collection and enrich StateManager.banks so credit-app selectors reflect it.
  React.useEffect(() => {
    store.getLenders()
      .then(lenders => StateManager.applyLenderConfig(lenders))
      .catch(() => {});
  }, []);

  // Keep modal-open ref in sync so the title guard above works without stale closure
  React.useEffect(() => {
    carModalOpenRef.current = !!carModalStock;
  }, [carModalStock]);

  // Sync URL params → modal state. Modal z-index is allocated per-instance via
  // useAutoZ (see ModalStackContext) so opening any modal automatically lifts
  // it above any older one — no manual stack bookkeeping required here.
  React.useEffect(() => {
    const sync = (location) => {
      const params = new URLSearchParams(location.search);
      const carParam = params.get('car') || null;
      const customerParam = constants.isValidCustomerId(params.get('customer')) ? params.get('customer') : null;
      const soParam = isOnSoNativePage(location.pathname) ? null : (params.get('so') || null);
      const soTabsParam = isOnSoNativePage(location.pathname) ? null : params.get('soTabs');
      const soTabs = soTabsParam ? soTabsParam.split(',').filter(Boolean) : null;
      const loadParam = isOnLoadNativePage(location.pathname) ? null : (params.get('load') || null);
      const creditAppParam = params.get('capp') || null;
      const leadParam = params.get('lead') || null;

      setCarModalStock(carParam);
      setCustomerModalId(customerParam);
      setSoModalStock(soParam);
      setSoModalAllowedTabs(soTabs);
      setLoadModalId(loadParam);
      setCreditAppModalId(creditAppParam);
      setLeadModalId(leadParam);

      sessionStorage.setItem('lastPath', location.pathname + location.search
        .replace(/[?&]car=[^&]*/g, '')
        .replace(/[?&]customer=[^&]*/g, '')
        .replace(/[?&]customerTab=[^&]*/g, '')
        .replace(/[?&]so=[^&]*/g, '')
        .replace(/[?&]soTabs=[^&]*/g, '')
        .replace(/[?&]load=[^&]*/g, '')
        .replace(/[?&]capp=[^&]*/g, '')
        .replace(/[?&]lead=[^&]*/g, '')
        .replace(/[?&]leadTab=[^&]*/g, '')
        .replace(/[?&]tab=[^&]*/g, '')
        .replace(/^&/, '?'));
    };
    sync(window.location);
    return history.listen(sync);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    console.log(title)
    document.title = title;
  }, [title])

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const handleTakePhoto = (dataUri) => {
    console.log("start", StateManager.photoHandler)
    StateManager.photoHandler(dataUri);
    // StateManager.toggleCamera();
  }

  // Print pages render without any app chrome
  if (window.location.pathname.endsWith('/print')) {
    return (
      <PermissionsContext.Provider value={permissions}>
        <CssBaseline />
        {!authReady
          ? <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></div>
          : <Router history={history}>
              <React.Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></div>}>
                <Switch>
                  <ProtectedRoute path="/credit-app/:id/print" render={props => <CreditAppPrintPage {...props} />} />
                  <ProtectedRoute path="/receipt/deposit/:depositId/print" permission="inventory" render={props => <DepositReceipt {...props} />} />
                  <ProtectedRoute path="/receipt/:kind(car|order)/:id/statement/print" permission="inventory" render={props => <PaymentStatement {...props} />} />
                  <ProtectedRoute path="/receipt/:kind(car|order)/:id/invoice/print" permission="inventory" render={props => <InvoiceRecap {...props} />} />
                </Switch>
              </React.Suspense>
            </Router>
        }
      </PermissionsContext.Provider>
    );
  }

  return (
    <PermissionsContext.Provider value={permissions}>
    <div className="App" style={{width: size.width, height: size.height}}>
      <CssBaseline />
      <DevBranchBadge />
      <Backdrop style={{zIndex: 999999, color: '#fff'}} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Backdrop style={{zIndex: 999999, color: '#fff'}} open={showCamera}>
        <div style={{backgroundColor: "#fff", zIndex: 99999999, position:"absolute", right: 10, top: 10}}>
          <IconButton onClick={StateManager.toggleCamera} size="large">
            <CloseIcon color='secondary' />
          </IconButton>
        </div>
        {
          showCamera && <Camera
            isFullscreen
            isSilentMode
            isMaxResolution
            idealFacingMode = {FACING_MODES.ENVIRONMENT}
            onTakePhoto = { (dataUri) => { handleTakePhoto(dataUri); } }
          />
        }
      </Backdrop>
      <Header title={title} />

      <div className={classes.root}>
        <main className={classes.content}>
          <Container className={`${classes.container}`} maxWidth={maxWidth}>
            {!authReady && (
              <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh'}}>
                <CircularProgress />
              </div>
            )}
            {authReady && <Router history={history}>
              <div className="section-to-print">
                <React.Suspense fallback={<div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh'}}><CircularProgress /></div>}>
                <Switch>
                  {/* Protected Pages */}
                  <ProtectedRoute path="/form/:formName" render={props => (
                    <InputPage {...props} />
                  )} />
                  <ProtectedRoute path="/service-board" render={props => <ServiceBoard {...props} />} />
                  <ProtectedRoute path="/customer/:id" render={props => {
                    const customerId = props.match.params.id;
                    const lastPath = sessionStorage.getItem('lastPath') || '/';
                    const base = lastPath.startsWith('/customer/') ? '/' : lastPath;
                    const sep = base.includes('?') ? '&' : '?';
                    return <Redirect to={`${base}${sep}customer=${customerId}`} />;
                  }} />
                  <ProtectedRoute path="/accounting" render={props => <AccountingPage {...props} />} />
                  <ProtectedRoute path="/signature-templates" permission="esign-templates" render={props => <SignatureTemplatesPage {...props} />} />
                  <ProtectedRoute path="/feed-health" permission="feed-health" render={props => <FeedHealthPage {...props} />} />
                  <ProtectedRoute path="/inventory-email" permission="inventory-email" render={props => <InventoryEmailPage {...props} />} />
                  <ProtectedRoute path="/deal-dashboard/:startDate/:endDate" render={props => <DealDashboardPage {...props} />} />
                  <ProtectedRoute path="/deal-dashboard/:month" render={props => {
                    const month = props.match.params.month;
                    const startDate = moment(month).startOf('month').format('YYYY-MM-DD');
                    const endDate = moment(month).endOf('month').format('YYYY-MM-DD');
                    return <Redirect to={`/deal-dashboard/${startDate}/${endDate}`} />;
                  }} />
                  <ProtectedRoute path="/performance-dashboard/:startMonth/:endMonth" render={props => <PerformanceDashboard {...props} />} />
                  <ProtectedRoute path="/deal-tracker" permission="sales-dashboard" render={props => <DealTrackerPage {...props} />} />
                  {/* The old Sales Dashboard was a two-tab shell around the same
                      leads list twice. The Deal Tracker replaces it and reuses its
                      permission key, so nobody's whiteList needs editing. */}
                  <ProtectedRoute path="/sales-dashboard/" render={() => <Redirect to="/deal-tracker" />} />
                  <ProtectedRoute path="/mechanic-summary/:periodStart" render={props => <MechanicSummaryPage {...props} />} />
                  <ProtectedRoute exact path="/mechanic-summary" render={() => <Redirect to={`/mechanic-summary/${constants.getDefaultPayPeriod().startParam}`} />} />
                  <ProtectedRoute path="/pipeline/" render={props => <PipelinePage {...props} />} />
                  {/* <ProtectedRoute path="/service-pipeline/" render={props => <ServicePipelinePage {...props} />} /> */}
                  <ProtectedRoute path="/service-aging/" render={props => <ServiceAgingPage {...props} />} />
                  <ProtectedRoute path="/mechanic-queue" render={props => <MechanicQueuePage {...props} />} />
                  <ProtectedRoute path="/service-pipeline/" render={props => <ServiceDashboardPage {...props} />} />
                  <ProtectedRoute path="/service-dashboard/" render={props => <ServiceDashboardPage {...props} />} />
                  {/* <ProtectedRoute path="/parts-pipeline/" render={props => <PartsPipelinePage {...props} />} /> */}
                  <ProtectedRoute path="/parts-pipeline/" render={props => <PartsDashboardPage {...props} />} />
                  <ProtectedRoute path="/parts-dashboard/" render={props => <PartsDashboardPage {...props} />} />
                  {/* Mechanics, service and parts users need this (their presets carry
                      'parts-inventory'), but it was a plain <Route>, so it was open to
                      everyone. */}
                  <ProtectedRoute path="/parts-inventory" permission="parts-inventory" render={props => <PartsInventoryPage {...props} />} />
                  <ProtectedRoute path="/shipping-dashboard/" render={props => <ShippingDashboardPage {...props} />} />
                  {/* Map picker as its own page — builds a brand-new load from the pins.
                      Its own permission, separate from the shipping dashboard: the
                      dashboard shows revenue and profit, the map shows neither, so
                      people who plan routes don't have to see the money. */}
                  <ProtectedRoute path="/load-planner" permission="load-planner" render={props => <LoadPlannerPage {...props} />} />
                  <Route path="/paint-jobs-dashboard/" render={props => <PaintJobsDashboardPage {...props} />} />
                  <ProtectedRoute path="/backoffice/" render={props => <BackOfficePage {...props} />} />
                  <ProtectedRoute path="/timesheets/:date/" render={props => <TimesheetPage {...props} />} />
                  <ProtectedRoute path="/purchase_orders" render={props => <ListPage type="purchase-orders" {...props} />} />
                  <ProtectedRoute path="/leads" render={props => <LeadsPage {...props} />} />
                  <ProtectedRoute path="/deposits" render={props => <ListPage type="deposits" {...props} />} />
                  <ProtectedRoute path="/payroll/:date" render={props => <PayrollPage {...props} />} />
                  <ProtectedRoute path="/credit-apps" render={props => <CreditAppsPage {...props} />} />
                  <ProtectedRoute path="/inspection" render={props => <InspectionPage {...props} />} />
                  <ProtectedRoute path="/inventory" render={props => <InventoryPage {...props} />} />
                  <ProtectedRoute path="/assign-part/:entryID" render={props => <AssignInventoryPage {...props} />} />
                  <ProtectedRoute path="/check-form/" render={props => <CheckForm {...props} />} />
                  <ProtectedRoute path="/parts-card-statement" render={props => <CreditCardStatementPage {...props} />} />
                  <ProtectedRoute path="/sales-inbox" render={props => <SalesInboxPage {...props} />} />
                  {/* Shop-line texts. access() covers staff who already hold a
                      floor permission, so Phone works before role presets are
                      reapplied. Vendor and driver presets do not qualify. */}
                  <ProtectedRoute path="/phone/:threadId" access={phoneAccess} render={props => <PhonePage {...props} />} />
                  <ProtectedRoute exact path="/phone" access={phoneAccess} render={props => <PhonePage {...props} />} />
                  <ProtectedRoute path="/consignment-dashboard" render={props => <ConsignmentDashboardPage {...props} />} />
                  <ProtectedRoute path="/search" render={props => <SearchPage {...props} />} />
                  <ProtectedRoute path="/files" permission="documents" render={props => <FilesPage {...props} />} />
                  <ProtectedRoute path="/bills" permission="accounting" render={props => <BillsPage {...props} />} />
                  <ProtectedRoute path="/car-performance" render={props => <CarPerformancePage {...props} />} />
                  <ProtectedRoute path="/inventory-snapshot" render={props => <InventorySnapshotPage {...props} />} />
                  <ProtectedRoute path="/accounts-payable" render={props => <AccountsPayablePage {...props} />} />
                  <ProtectedRoute path="/accounts-receivable" render={props => <AccountsReceivablePage {...props} />} />
                  <ProtectedRoute path="/lead-sources" render={props => <LeadSourcesPage {...props} />} />
                  <ProtectedRoute path="/rep-performance" permission="deal-dashboard" render={props => <RepPerformanceDashboard {...props} />} />
                  <ProtectedRoute path="/contractor-payment/:employeeId?/:paymentDate?" render={props => <ContractorPaymentPage {...props} />} />
                  <ProtectedRoute path="/credit-app/:id/print" render={props => <CreditAppPrintPage {...props} />} />
                  <ProtectedRoute path="/credit-app-history" permission="credit-apps" render={props => <CreditAppSendsPage {...props} />} />
                  <ProtectedRoute path="/title-processing" render={props => <TitleProcessingPage {...props} />} />
                  <ProtectedRoute path="/user-management" render={props => <UserManagementPage {...props} />} />
                  <ProtectedRoute path="/lenders" permission="bank-management" render={props => <BanksPage {...props} />} />
                  <ProtectedRoute path="/lead-assignment" render={props => <LeadAssignmentPage {...props} />} />
                  <ProtectedRoute path="/ron-checkin" permission="ron-checkin" render={props => <RonCheckinPage {...props} />} />
                  <ProtectedRoute path="/price-strategy" permission="price-strategy" render={props => <PriceStrategyPage {...props} />} />
                  <ProtectedRoute path="/reports/service-performance/:startDate/:endDate" permission="accounting" render={props => <ServicePerformancePage {...props} />} />
                  <ProtectedRoute path="/reports/service-performance" permission="accounting" render={props => <ServicePerformancePage {...props} />} />
                  <ProtectedRoute path="/reports/sales-performance/:startDate/:endDate" permission="accounting" render={props => <SalesPerformancePage {...props} />} />
                  <ProtectedRoute path="/reports/sales-performance" permission="accounting" render={props => <SalesPerformancePage {...props} />} />
                  <ProtectedRoute path="/reports/inventory-performance/:startDate/:endDate" permission="accounting" render={props => <InventoryPerformancePage {...props} />} />
                  <ProtectedRoute path="/reports/inventory-performance" permission="accounting" render={props => <InventoryPerformancePage {...props} />} />
                  <ProtectedRoute path="/reports/car-demand" permission="accounting" render={props => <CarDemandPage {...props} />} />
                  <ProtectedRoute path="/reports/funding-performance" permission="accounting" render={props => <FundingPerformancePage {...props} />} />
                  <ProtectedRoute path="/reports/inventory-distribution" permission="accounting" render={props => <InventoryDistributionPage {...props} />} />
                  <ProtectedRoute path="/sales-tax/:startDate/:endDate" permission="accounting" render={props => <SalesTaxPage {...props} />} />
                  <ProtectedRoute path="/sales-tax" permission="accounting" render={props => <SalesTaxPage {...props} />} />
                  {/* unprotected pages */} 
                  <Route path="/service-priorities" render={props => <ServicePrioritiesPage {...props} />} />
                  <Route path="/service-plan" render={props => <ServicePlanPage {...props} />} />
                  <Route path="/estimate" render={props => <EstimatePage {...props} />} />
                  <Route path="/service-status" render={props => <ServiceStatusPage {...props} />} />
                  {/* These three were plain <Route>, i.e. reachable with no login at
                      all, and only worked because the Firestore rules were wide open.
                      Mechanics now sign in with a PIN (see MechanicsManager), so they
                      are gated like every other page. */}
                  <ProtectedRoute path="/mechanic-orders" permission="mechanic-orders" render={props => <MechanicOrdersPage {...props} />} />
                  <ProtectedRoute path="/mechanic-clock" permission="mechanic-clock" render={props => <MechanicClockPage {...props} />} />
                  <ProtectedRoute path="/mechanic-board" permission="mechanic-board" render={props => <MechanicBoardPage {...props} />} />
                  <ProtectedRoute path="/intake-queue" render={props => <IntakeQueuePage {...props} />} />
                  <Route path="/payment" render={props => <PaymentPage {...props} />} />
                  <Route path="/payment-status" render={props => <PaymentConfirmationPage {...props} />} />
                  <Route path="/thankyou" render={props => <ThankYouPage {...props} />} />
                  <Route path="/new-user" render={() => <Redirect to="/user-management" />} />
                  <Route path="/scan" render={props => <ScanPage {...props} />} />
                  {/* Vendor pages were public and keyed off the :vendor URL segment,
                      so any vendor could read another's data by editing the URL.
                      Vendors now get their own PIN login carrying a `vendor` field,
                      which the pages below use instead of trusting the URL. */}
                  <ProtectedRoute path="/vendor-items/:vendor" permission="vendor-dashboard" render={props => <VendorPage {...props} />} />
                  <ProtectedRoute path="/vendor-dashboard/:vendor?" permission="vendor-dashboard" render={props => <VendorDashboard {...props} />} />
                  <Route path="/shipping-submit/" render={props => <ShppingForm {...props} />} />
                  <Route path="/input/" render={props => <InputPage {...props} page={"edit-vendor-item"} />} />
                  {/* Was public (carriers used to open load links with no account).
                      Now gated on 'load', which is deliberately the ONLY permission an
                      outside carrier needs — it grants this page and the load modal and
                      nothing else. */}
                  <ProtectedRoute path="/load/:loadId" permission="load" render={props => <ShippingLoadPage {...props} />} />
                  <Route path="/add-lead" render={props => <AddLeadPage {...props} />} />
                  {/* Was a plain <Route>, so it was open to everyone regardless of
                      permissions — the "always on" Detail Dashboard. */}
                  <ProtectedRoute path="/detail-dashboard" permission="detail-dashboard" render={props => <DetailDashboardPage {...props} />} />
                  {/* redirect user to page below if route does not exist */} 
                  <Route path="/" render={props => StateManager.authed ? getHomePage(props) : <LoginPage {...props} />} />
                </Switch>
                </React.Suspense>
              </div>
            </Router>}
          </Container>
        </main>
      </div>

      <Snackbar open={open} autoHideDuration={3000} onClose={handleClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} sx={{ zIndex: 2147483646 }}>
        <MuiAlert elevation={6} variant="filled" onClose={handleClose} severity={severity} >
          {message}
        </MuiAlert>
      </Snackbar>
      <PhoneInboundAlerts />

      <AdminToast />
      <MechanicToast />

      {/* The global modals are mounted here for every page, and each is driven by a
          URL param. That made them a way around the permission system: ProtectedRoute
          gates PAGES, but ?customer=<id> on any page a user can already reach opened
          the customer modal regardless of their whiteList — which is how a mechanic
          with no 'customer' permission could read customer records. Each modal is now
          gated on the same key as its page, so the param does nothing without it.
          Admins bypass, matching ProtectedRoute (isAllowed(key) || isAdmin()). */}
      <CarModal
        open={!!carModalStock && canOpen('car')}
        stockNumber={carModalStock}
        onClose={closeCarModal}
      />

      <ServiceOrderModal
        open={!!soModalStock && canOpen('service-order')}
        stockNumber={soModalStock}
        onClose={closeSoModal}
        allowedTabs={soModalAllowedTabs}
      />

      {/* Global admin-only Write Check modal — opens over any page/modal */}
      <WriteCheckModal
        open={!!checkPurchases}
        purchases={checkPurchases || []}
        onClose={closeWriteCheckModal}
      />

      {/* Global admin-only Pay NTO modal — opens over any page/modal */}
      <PayNtoModal
        open={!!ntoPayable}
        payable={ntoPayable}
        onClose={closePayNtoModal}
      />

      {/* Global customer modal — opens over any page via ?customer= URL param */}
      <CustomerModal
        open={!!customerModalId && canOpen('customer')}
        customerId={customerModalId}
        onClose={closeCustomerModal}
      />

      {/* Global lead modal — opens over any page via ?lead= URL param */}
      <LeadModal
        open={!!leadModalId && canOpen('lead')}
        leadId={leadModalId}
        onClose={closeLeadModal}
      />

      {/* Global credit-app modal — opens over any page via ?creditApp= URL param */}
      <CreditAppModal
        open={!!creditAppModalId && canOpen('credit-app')}
        creditAppId={creditAppModalId}
        onClose={closeCreditAppModal}
      />

      {/* Global shipping-load modal — opens over any page via ?load= URL param */}
      <ShippingLoadModal
        open={!!loadModalId && canOpen('load')}
        loadId={loadModalId}
        onClose={closeLoadModal}
        onDeleted={closeLoadModal}
      />
    </div>
    </PermissionsContext.Provider>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  content: {
    flexGrow: 1,
    height: '100%',
    overflow: 'auto',
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
}));

const phoneAccess = (perms) => canAccessPhone({
  userType: perms.userType,
  whiteList: perms.whiteList,
  isAdmin: perms.isAdmin(),
});

const ProtectedRoute = ({ permission, access, ...props }) => {
  const perms = usePermissions();
  const { isAllowed, isAdmin, ready } = perms;
  const isAuthed = localStorage.getItem('authed') || sessionStorage.getItem('authed');
  const permissionKey = permission || props.path.replace("/", "").split("/")[0];
  const canAccess = typeof access === 'function'
    ? !!access(perms)
    : (isAllowed(permissionKey) || isAdmin());

  // Signed in, but we don't know what they're allowed to see yet. Wait. Redirecting
  // here would read to the user as being thrown off their page for no reason — and
  // they'd be right, because the answer was seconds away. `ready` is guaranteed to
  // arrive (snapshot, listen error, or the 10s fallback in App), so this can't hang.
  if (isAuthed && !ready) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </div>
    );
  }

  // Not signed in yet: remember where they were trying to go so login can finish the
  // journey. Only for the not-authed case — someone who IS signed in but lacks the
  // permission should just go home, not be sent back to a page they can't have.
  if (!isAuthed) {
    savePostLoginRedirect(window.location.pathname + window.location.search);
  }

  return(
    isAuthed && canAccess
      ? <Route {...props} />
      : <Redirect to={{pathname: "/"}} />
  )
}

const getHomePage = (props) => {
  let page = <InventoryPage {...props} />
  switch (StateManager.userType) {
    case "mechanic":
      page = <MechanicOrdersPage {...props} />
      break;

    case "sales":
      page = <InventoryPage {...props} />
      break;

    case "service":
      page = <ServicePipelinePage {...props} />
      break;

    // Outside contractors must not land on the inventory page. This renders without
    // a permission check (it's also where ProtectedRoute redirects on denial), so the
    // default would otherwise show a vendor the whole inventory.
    case "vendor":
      page = <VendorDashboard {...props} />
      break;

    default:
      // The default is the inventory list, but this function renders WITHOUT any
      // permission check and is where ProtectedRoute sends anyone it denies. A
      // link-only account (an outside carrier holding just 'load') or a user with no
      // permissions at all would otherwise be shown the entire inventory.
      if (!StateManager.isAdmin() && !(StateManager.whiteList || []).includes('inventory')) {
        page = <NoAccessHome />;
      }
      break;
  }

  return page;
}

// Landing page for accounts that have no browsable pages — link-only carriers, or a
// user an admin hasn't finished setting up.
const NoAccessHome = () => (
  <Container maxWidth="sm" style={{ marginTop: 64 }}>
    <Paper style={{ padding: 32, textAlign: 'center' }}>
      <Typography variant="h6" gutterBottom>Nothing to show here</Typography>
      <Typography variant="body2" color="textSecondary">
        Your account doesn't have any pages assigned. If you were sent a link, open that
        link directly. Otherwise ask a manager to grant you access.
      </Typography>
    </Paper>
  </Container>
);

export default App;
